import { useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { Section, OptionalTag, fileSrc } from "./builderUi";

const MAX_PHOTOS = 6;
const client = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("mra_jwt");
  if (t) { cfg.headers = cfg.headers || {}; cfg.headers.Authorization = `Bearer ${t}`; }
  return cfg;
});

/** Gallery photos upload one at a time, then the id list is saved onto the campaign. */
export const PhotosSection = ({ photoIds = [], onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const remaining = MAX_PHOTOS - photoIds.length;

  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, Math.max(remaining, 0));
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError("");
    const next = [...photoIds];
    try {
      for (const f of files) {
        if (f.size > 8 * 1024 * 1024) { setError(`${f.name} is over 8 MB.`); continue; }
        const form = new FormData();
        form.append("purpose", "campaign_photo");
        form.append("file", f);
        const res = await client.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(res.data.id);
      }
      if (next.length !== photoIds.length) await onChange(next);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try { await client.delete(`/files/${id}`); } catch { /* soft-delete is best effort */ }
    await onChange(photoIds.filter((x) => x !== id));
  };

  return (
    <Section accent="bg-tangerine" title="Photos" subtitle="A few photos help donors get to know you. Up to six." badge={<OptionalTag />} testid="builder-photos">
      {remaining > 0 && (
        <button
          type="button"
          data-testid="builder-add-photos"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-secondary/40 px-6 py-8 text-center transition-colors hover:border-scarlet/50 hover:bg-secondary/70 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-7 w-7 animate-spin text-scarlet" /> : <ImagePlus className="h-7 w-7 text-scarlet" strokeWidth={1.5} />}
          <span className="text-[13px] font-semibold text-ink">{busy ? "Uploading…" : "Add photos"}</span>
          <span className="text-[13px] text-olive">PNG, JPG or WebP. {remaining} more allowed.</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={addFiles} data-testid="builder-photos-input" />
      {error && <p role="alert" className="mt-3 text-[13px] text-scarlet" data-testid="builder-photos-error">{error}</p>}
      {photoIds.length > 0 && (
        <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="builder-photo-grid">
          {photoIds.map((id) => (
            <div key={id} data-testid="builder-photo-thumb" className="group relative aspect-square overflow-hidden rounded-md border border-border">
              <img src={fileSrc(id)} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                aria-label="Remove photo"
                onClick={() => remove(id)}
                className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink/80 text-sand opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};
